import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function WorkspaceContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">工作区</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          智能体工作区是存储配置、记忆、技能和会话数据的本地目录，是智能体持久化状态的核心。
        </p>
      </div>

      <SectionTitle id="ws-default">默认位置</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        工作区默认存储在用户主目录下：
      </p>
      <CodeBlock language="bash" code={`# macOS / Linux
~/.openclaw/agents/<agent-id>/

# Windows
%USERPROFILE%\\.openclaw\\agents\\<agent-id>\\`} />
      <InfoBox type="info">
        <code className="text-cyan-400 bg-slate-800/60 px-1 rounded text-xs">&lt;agent-id&gt;</code> 是智能体的唯一标识符，由配置文件中的 <code className="text-cyan-400 bg-slate-800/60 px-1 rounded text-xs">id</code> 字段决定。
      </InfoBox>

      <SectionTitle id="ws-extra">额外的工作区文件夹</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        可以在配置中指定额外的工作区文件夹，智能体可以访问这些目录中的文件：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    workspace: {
      // 主工作区（默认）
      root: "~/.openclaw/agents/my-agent",
      // 额外的只读目录
      extraDirs: [
        "/path/to/shared-knowledge",
        "/path/to/project-docs",
      ],
      // 额外的可写目录
      writableDirs: [
        "/path/to/output",
      ]
    }
  }
}`} />

      <SectionTitle id="ws-mapping">工作区文件映射</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        工作区中的标准文件及其用途：
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">文件</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">用途</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">是否自动创建</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['agent.json5', '智能体主配置文件', '是'],
              ['memory.md', '长期记忆文件（Markdown 格式）', '是'],
              ['skills/', '技能脚本目录', '是'],
              ['sessions/', '会话数据存储目录', '是'],
              ['.env', '环境变量（API 密钥等）', '否，需手动创建'],
              ['.gitignore', 'Git 忽略规则', '是（包含 .env 等敏感文件）'],
            ].map(([file, usage, auto], i) => (
              <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="py-2.5 px-3"><code className="text-cyan-400 font-mono text-xs">{file}</code></td>
                <td className="py-2.5 px-3 text-slate-300">{usage}</td>
                <td className="py-2.5 px-3 text-slate-400 text-xs">{auto}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionTitle id="ws-excluded">工作区中不包含的内容</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        以下内容不存储在工作区中：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-close-circle-line text-rose-400 mt-0.5 flex-shrink-0"></i><span>OAuth 令牌（存储在系统密钥链中）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-close-circle-line text-rose-400 mt-0.5 flex-shrink-0"></i><span>模型 API 密钥（建议存储在 .env 文件中，不提交到 Git）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-close-circle-line text-rose-400 mt-0.5 flex-shrink-0"></i><span>渠道配对信息（存储在系统密钥链中）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-close-circle-line text-rose-400 mt-0.5 flex-shrink-0"></i><span>向量嵌入缓存（存储在单独的缓存目录中）</span></li>
      </ul>

      <SectionTitle id="ws-git">Git 备份</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        工作区支持 Git 版本控制，可以追踪配置和记忆文件的变更历史：
      </p>
      <CodeBlock language="bash" code={`# 初始化 Git 仓库
cd ~/.openclaw/agents/my-agent
git init
git add agent.json5 memory.md skills/
git commit -m "初始化工作区"

# 推送到远程仓库（用于备份）
git remote add origin https://github.com/your/agent-backup.git
git push -u origin main`} />
      <InfoBox type="tip">
        OpenClaw 会自动在工作区创建 <code className="text-cyan-400 bg-slate-800/60 px-1 rounded text-xs">.gitignore</code> 文件，排除敏感文件（如 .env、会话数据等）。
      </InfoBox>

      <SectionTitle id="ws-no-secrets">不要提交密钥</SectionTitle>
      <InfoBox type="warning">
        <strong>重要：</strong>永远不要将 API 密钥、OAuth 令牌或其他敏感信息提交到 Git 仓库。
        使用 <code className="text-amber-400 bg-slate-800/60 px-1 rounded text-xs">.env</code> 文件存储密钥，并确保 <code className="text-amber-400 bg-slate-800/60 px-1 rounded text-xs">.env</code> 已添加到 <code className="text-amber-400 bg-slate-800/60 px-1 rounded text-xs">.gitignore</code>。
      </InfoBox>
      <CodeBlock language="bash" code={`# .gitignore 示例
.env
.env.*
sessions/
*.key
*.pem
auth/`} />

      <SectionTitle id="ws-migrate">将工作区迁移到新机器</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        迁移工作区到新机器的步骤：
      </p>
      <div className="space-y-3 my-4">
        {[
          ['导出工作区', 'openclaw workspace export --agent my-agent --output backup.tar.gz'],
          ['在新机器上安装 OpenClaw', 'npm install -g openclaw'],
          ['导入工作区', 'openclaw workspace import --file backup.tar.gz'],
          ['重新配置密钥', '在新机器上重新设置 API 密钥和 OAuth 令牌'],
          ['重新配对渠道', '对需要配对的渠道（如 WhatsApp）重新扫码'],
        ].map(([title, cmd], i) => (
          <div key={title} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <div>
              <div className="text-white font-medium text-sm mb-1">{title}</div>
              <code className="text-xs text-slate-400 font-mono bg-slate-800/60 px-2 py-1 rounded">{cmd}</code>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="ws-advanced">高级注意事项</SectionTitle>
      <ul className="space-y-2.5 text-sm text-slate-300">
        <li className="flex items-start gap-2.5">
          <i className="ri-information-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">符号链接：</strong>工作区支持符号链接，可以将大型文件或目录链接到工作区中而不复制。</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-information-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">多智能体共享：</strong>多个智能体可以通过 extraDirs 共享同一个知识库目录。</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-information-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">文件监听：</strong>OpenClaw 会监听工作区文件变化，配置文件修改后会自动热重载（无需重启）。</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-information-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">权限：</strong>确保 OpenClaw 进程对工作区目录有读写权限，否则会导致会话保存失败。</span>
        </li>
      </ul>
    </div>
  );
}
